import { AvailableAppConnector } from '@components/connectors/Summary'
import { venueSchema } from '@models/schema'
import { CreateVenueData, Venue } from '@models/Venue'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createVenue as createVenueAPI,
  deleteVenue as deleteVenueAPI,
  fetchVenueBySlug,
  fetchVenues,
  updateVenue as updateVenueAPI,
} from '@services/VenueService'
import {
  baseReducers,
  deepMerge,
  getIdOrModelId,
  getSlugOrModelSlug,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { AppDispatch, RootState } from './store'

const initialState: SBState<Venue> & { selectedSlug: string | undefined } = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  selectedSlug: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'venue',
  initialState,
  reducers: {
    ...baseReducers,
    setSelectedSlug: (state, action) => ({
      ...state,
      selectedSlug: action.payload,
    }),
  },
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getVenuesSuccess,
  setQuery: setVenueQuery,
  reset: resetVenueState,
  resetQueryAndIds: resetVenueQueryAndIds,
  updateItemSuccess: updateVenueLocally,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Venue }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectVenues = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedVenue = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectVenueById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectVenuesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
export const selectVenueBySlug = (slug: string) =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items)
      .map((i) => i[1])
      .find((venue) => venue.slug === slug)
  )

/**
 * Actions
 */

export const setSelectedVenue =
  (venue: Venue | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Venue>(venue)))

export const setSelectedVenueWithSlug =
  (venue: Venue | string) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedSlug(getSlugOrModelSlug<Venue>(venue)))

export const getVenues = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchVenues(params),
    [venueSchema],
    slice.actions
  )

export const createVenue = (venue: CreateVenueData) =>
  SBAPI.CreateDispatched(createVenueAPI(venue), venueSchema, slice.actions)

export const getVenueWithSlug = (slug: string) =>
  SBAPI.FetchDispatched<Venue>(
    fetchVenueBySlug(slug),
    venueSchema,
    slice.actions
  )

export const partialVenueUpdate = (venue: Venue, updates: Partial<Venue>) =>
  SBAPI.UpdateDispatched<Venue>(
    updateVenueAPI(updates),
    deepMerge<Venue>(venue, updates),
    slice.actions
  )

export const deleteVenue = (venue: Venue) =>
  SBAPI.DestroyDispatched<Venue>(deleteVenueAPI(venue), venue, slice.actions)

export const setActiveAppConnector = (
  venue: Venue,
  nextConnector: AvailableAppConnector
) =>
  partialVenueUpdate(venue, {
    id: venue.id,
    slug: venue.slug,
    booking: {
      app: {
        activeConnector: nextConnector,
      },
    },
  } as any)

export const resetActiveAppConnector = (venue: Venue) =>
  partialVenueUpdate(venue, {
    id: venue.id,
    slug: venue.slug,
    booking: {
      app: {
        activeConnector: null,
      },
    },
  } as any)
